import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { PublicProfile, publicProfileSchema } from '../models/PublicProfile';
import { database } from '../lib/supabase';

type PublicProfileContextType = {
    publicProfile: PublicProfile | null;
    isLoading: boolean;
    error: string | null;
    setPublished: (published: boolean) => Promise<void>;
};

const PublicProfileContext = createContext<PublicProfileContextType | undefined>(undefined);

export const usePublicProfile = () => {
    const context = useContext(PublicProfileContext);
    if (!context) {
        throw new Error('usePublicProfile must be used within a PublicProfileProvider');
    }
    return context;
};

type PublicProfileProviderProps = PropsWithChildren<{
    userKey?: string;
    userId?: string;
}>;

export const PublicProfileProvider: React.FC<PublicProfileProviderProps> = ({ children, userKey, userId }) => {
    const [publicProfile, setPublicProfile] = useState<PublicProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPublicProfile = async () => {
            if (!userKey && !userId) {
                setError('No user key or ID provided');
                setIsLoading(false);
                return;
            }

            try {
                let query = database.publicProfiles.table()
                    .select('*');

                if (userKey) {
                    query = query.contains('link_keys', [userKey]);
                } else if (userId) {
                    query = query.eq('user_id', userId);
                }

                const { data, error: dbError } = await query.single();

                if (dbError) {
                    throw dbError;
                }

                if (!data) {
                    throw new Error('Profile not found');
                }

                const parsedProfile = publicProfileSchema.parse(data);
                setPublicProfile(parsedProfile);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
                console.error(err);
                setPublicProfile(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPublicProfile();
    }, [userKey, userId]);

    const setPublished = async (published: boolean) => {
        if (!publicProfile) {
            throw new Error('No profile loaded');
        }

        try {
            setIsLoading(true);
            const { error: dbError } = await database.publicProfiles.table()
                .update({ is_published: published })
                .eq('user_id', publicProfile.user_id);

            if (dbError) {
                throw dbError;
            }

            // Update local state
            setPublicProfile({
                ...publicProfile,
                is_published: published
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update profile visibility');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PublicProfileContext.Provider value={{ publicProfile, isLoading, error, setPublished }}>
            {children}
        </PublicProfileContext.Provider>
    );
};
